import {
  type ViewerMemoryManagerProps,
  type ViewerMemoryConfig,
  type ViewerMemoryConfigMap,
} from './ViewerMemoryManager';
import { useViewerId } from 'hooks/useViewerId';
import { viewportIdToConfig } from './viewerUtils';
import { useMemo } from 'react';
import {
  ImagingProviderLoadType,
  type BaseImagingProvider,
} from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { MultiStackImagingProvider } from '../ViewportDre/modules/imaging/MultiStackImagingProvider';
import { MultiObjectImagingProvider } from '../ViewportDre/modules/imaging/MultiObjectImagingProvider';
import type { Stack } from '../ViewportsConfigurations/types';
import { SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT } from 'config/constants';
import { logger } from 'modules/logger';
import { FF, useSplitFlag } from 'modules/feature-flags/suspense';

const ONE_GB = 1024 * 1024 * 1024;

// Chrome has a limit around 13.3 GB for Typed Arrays in a single tab
const CHROME_MEMORY_LIMIT = 13 * ONE_GB;

// dedicate 2 GB to a "buffer" for miscellaneous transfer overheard and garbage collection delays
const WORKING_SPACE = 4 * ONE_GB;

// future-proofing more complicated logic for determining a memory limit;
// confidence lost in worker oprhaning approach, will try to limit worker usage to balance the needs of both
const useMemoryLimit = () => {
  const memoryFlag = Number(useSplitFlag(FF.VIEWER_MEMORY_OFFSET_GB));
  const memoryOffset = Number.isNaN(memoryFlag) ? 0 : memoryFlag * ONE_GB;
  return (
    CHROME_MEMORY_LIMIT - SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT - WORKING_SPACE + memoryOffset
  );
};

// side-effect-free hook to determine what should be loaded at any given time
export function useViewerMemoryConfig({
  stackProviders,
  viewportsConfigurations,
  areViewportsReady,
}: ViewerMemoryManagerProps): ViewerMemoryConfig {
  const memoryLimit = useMemoryLimit();
  const viewerId = useViewerId();

  return useMemo(() => {
    // 1. Initialize
    const memoryConfigMap: ViewerMemoryConfigMap = new Map();
    // the second check is just for type safety and is one component of `areViewportsReady`
    if (!areViewportsReady || viewportsConfigurations == null) {
      return { memoryLimit, usedMemory: null, memoryConfig: memoryConfigMap };
    }

    // 2. separate out providers not in this window's viewport configuration
    // Composite viewports promote their referenced children so layers don't get demoted to None.
    const stackProvidersInViewports = new Set<BaseImagingProvider<Stack>>();
    const promoteProvider = (provider: BaseImagingProvider<Stack>) => {
      if (stackProvidersInViewports.has(provider)) return;
      stackProvidersInViewports.add(provider);
      if (
        provider instanceof MultiStackImagingProvider ||
        provider instanceof MultiObjectImagingProvider
      ) {
        for (const child of provider.getConstituentProviders()) promoteProvider(child);
      }
    };

    Object.entries(viewportsConfigurations).forEach(([viewportId, viewportConfiguration]) => {
      if (viewportIdToConfig(viewportId).windowId !== viewerId) return;
      const smid = viewportConfiguration?.stack?.smid;
      const provider = smid ? stackProviders.get(smid) : undefined;
      if (provider) promoteProvider(provider);
    });

    // Composite providers don't own pixel memory; excluding them avoids double-counting against their children.
    const stackProvidersOutsideViewports = new Set<BaseImagingProvider<Stack>>();
    stackProviders.forEach((p: BaseImagingProvider<Stack>) => {
      if (stackProvidersInViewports.has(p)) return;
      if (p instanceof MultiStackImagingProvider || p instanceof MultiObjectImagingProvider) return;
      stackProvidersOutsideViewports.add(p);
    });

    let remainingMemoryToUse = memoryLimit;

    // 3. Estimate hanged stack providers memory usage
    let idealMemoryForFullLoad = 0;
    const loadedStacksMemoryMap = new Map<BaseImagingProvider<Stack>, number>();
    stackProvidersInViewports.forEach((provider) => {
      const estimate = provider.estimateMemory();
      idealMemoryForFullLoad += estimate;
      loadedStacksMemoryMap.set(provider, estimate);
    });

    if (idealMemoryForFullLoad > remainingMemoryToUse) {
      stackProvidersInViewports.forEach((provider) => {
        const estimate = provider.estimateMemory();
        if (estimate <= remainingMemoryToUse) {
          loadedStacksMemoryMap.set(provider, estimate);
          remainingMemoryToUse -= estimate;
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
        } else {
          loadedStacksMemoryMap.set(provider, 0);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
        }
      });

      stackProvidersOutsideViewports.forEach((provider) => {
        loadedStacksMemoryMap.set(provider, 0);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
      });

      logger.info('[MEMORY CONFIG] partial-hanged only', {
        memoryLimit: `${memoryLimit / ONE_GB} GB`,
        idealMemoryForHangedLoad: `${idealMemoryForFullLoad / ONE_GB} GB`,
        remainingMemoryToUse: `${remainingMemoryToUse / ONE_GB} GB`,
        CHROME_MEMORY_LIMIT: `${CHROME_MEMORY_LIMIT / ONE_GB} GB`,
        WORKING_SPACE: `${WORKING_SPACE / ONE_GB} GB`,
        SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT: `${SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT / ONE_GB} GB`,
        memoryConfigMap: Object.fromEntries(memoryConfigMap),
      });

      return {
        memoryLimit,
        usedMemory: memoryLimit - remainingMemoryToUse,
        memoryConfig: memoryConfigMap,
      };
    }

    // 4. Allocate Hanged Stacks
    loadedStacksMemoryMap.forEach((stackMemory, provider) => {
      remainingMemoryToUse -= stackMemory;
      memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
    });

    let memoryForUnhangedStacksFullLoad = 0;
    stackProvidersOutsideViewports.forEach((provider) => {
      memoryForUnhangedStacksFullLoad += provider.estimateMemory();
    });
    idealMemoryForFullLoad += memoryForUnhangedStacksFullLoad;

    if (remainingMemoryToUse > memoryForUnhangedStacksFullLoad) {
      // 5. Allocate full memory for unhanged stacks
      stackProvidersOutsideViewports.forEach((provider) => {
        const estimate = provider.estimateMemory();
        loadedStacksMemoryMap.set(provider, estimate);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
      });
      remainingMemoryToUse -= memoryForUnhangedStacksFullLoad;
    } else {
      // 5. Allocate minimal memory for unhanged non3dable stacks
      stackProvidersOutsideViewports.forEach((provider) => {
        const estimatePerFrame = provider.estimatePerFrameMemory();

        if (remainingMemoryToUse >= estimatePerFrame && !provider.is3Dable()) {
          loadedStacksMemoryMap.set(provider, estimatePerFrame);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Initial);
          remainingMemoryToUse -= estimatePerFrame;
        } else {
          loadedStacksMemoryMap.set(provider, 0);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
        }
      });
    }

    logger.info('[MEMORY CONFIG]', {
      memoryLimit: `${memoryLimit / ONE_GB} GB`,
      idealMemoryForFullLoad: `${idealMemoryForFullLoad / ONE_GB} GB`,
      remainingMemoryToUse: `${remainingMemoryToUse / ONE_GB} GB`,
      memoryForHangedStacksFullLoad: `${(idealMemoryForFullLoad - memoryForUnhangedStacksFullLoad) / ONE_GB} GB`,
      memoryForUnhangedStacksFullLoad: `${memoryForUnhangedStacksFullLoad / ONE_GB} GB`,
      CHROME_MEMORY_LIMIT: `${CHROME_MEMORY_LIMIT / ONE_GB} GB`,
      WORKING_SPACE: `${WORKING_SPACE / ONE_GB} GB`,
      SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT: `${SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT / ONE_GB} GB`,
      memoryConfigMap: Object.fromEntries(memoryConfigMap),
    });

    return {
      memoryLimit,
      usedMemory: memoryLimit - remainingMemoryToUse,
      memoryConfig: memoryConfigMap,
    };
  }, [stackProviders, viewportsConfigurations, viewerId, areViewportsReady, memoryLimit]);
}
